/**
 * Global Search Pages List
 *
 * This file contains all searchable pages and settings for the GlobalSearch component.
 * When adding, removing, or modifying pages/settings:
 * - Update MoreRedesign.jsx if the page appears in settings
 * - Update More.jsx if the page appears in settings (non-redesign)
 * - Update App.jsx if the route needs to be added/modified
 * - Update NavRedesign.jsx if the page appears in navigation
 * - Ensure the route exists in App.jsx
 */

const pages = [
    {
        title: 'Dashboard',
        to: '/',
        keywords: ['home', 'dashboard', 'main'],
    },
    {
        title: 'Reports',
        to: '/reports',
        keywords: ['reports', 'analytics', 'statistics'],
    },
    {
        title: 'Customers',
        to: '/manage/customers',
        keywords: ['customers', 'client', 'contacts'],
        feature: 'CUSTOMERS',
    },
    {
        title: 'Quotes',
        to: '/manage/quotes',
        keywords: ['quotes', 'estimates'],
    },
    {
        title: 'Payments',
        to: '/manage/payments',
        keywords: ['payments', 'money', 'transactions'],
    },
    {
        title: 'Expenses',
        to: '/manage/expenses',
        keywords: ['expenses', 'costs', 'spending'],
        feature: 'EXPENSES',
    },
    {
        title: 'Broadcasts',
        to: '/more/broadcasts',
        keywords: ['broadcasts', 'messages', 'bulk'],
        feature: 'BROADCASTS',
    },
    {
        title: 'Leads',
        to: '/plan/leads',
        keywords: ['leads', 'prospects'],
    },
    {
        title: 'Calendar',
        to: '/plan/calendar',
        keywords: ['calendar', 'schedule', 'appointments'],
        feature: 'WEEK_PLANNER',
    },
    {
        title: 'Rounds',
        to: '/plan/rounds',
        keywords: ['rounds', 'routes'],
        feature: 'ROUNDS',
    },
    {
        title: 'Jobs',
        to: '/plan/jobs',
        keywords: ['jobs', 'work', 'tasks'],
    },
    {
        title: 'Admin Panel',
        to: '/admin',
        keywords: ['admin', 'administration'],
    },
    {
        title: 'Settings',
        to: '/more',
        keywords: ['settings', 'preferences', 'configuration'],
    },
    {
        title: 'Business Details',
        to: '/business/details',
        keywords: ['business', 'details', 'company', 'information'],
    },
    {
        title: 'Documents',
        to: '/business/documents',
        keywords: ['documents', 'files', 'templates'],
        feature: 'DOCUMENTS',
    },
    {
        title: 'Services',
        to: '/business/services',
        keywords: ['services', 'offerings'],
    },
    {
        title: 'Pricing',
        to: '/business/pricing',
        keywords: ['pricing', 'rates', 'prices'],
    },
    {
        title: 'Notifications',
        to: '/triggers',
        keywords: ['notifications', 'triggers', 'alerts', 'messages'],
        feature: 'CUSTOMER_NOTIFICATIONS',
    },
    {
        title: 'Customer Notifications',
        to: '/settings/customer-notifications',
        keywords: ['customer', 'notifications', 'alerts'],
        feature: 'CUSTOMER_NOTIFICATIONS',
    },
    {
        title: 'Message Templates',
        to: '/settings/message-templates',
        keywords: ['message', 'templates', 'sms', 'text'],
        feature: 'CUSTOMER_NOTIFICATIONS',
    },
    {
        title: 'Document Templates',
        to: '/settings/document-templates',
        keywords: ['document', 'templates', 'invoices'],
        feature: 'INVOICING',
    },
    {
        title: 'Custom SMS Name',
        to: '/business/custom-sms-name',
        keywords: ['sms', 'name', 'custom'],
        feature: 'SMS_CUSTOM_NAME',
    },
    {
        title: 'Setup Payments',
        to: '/settings/payments/setup',
        keywords: ['payments', 'setup', 'stripe', 'card'],
        feature: 'CARDS_PAYMENTS',
    },
    {
        title: 'Bank Account',
        to: '/settings/payments/bank-account',
        keywords: ['bank', 'account', 'payouts'],
        feature: 'CARDS_PAYMENTS',
    },
    {
        title: 'Payment Options',
        to: '/settings/payments/options',
        keywords: ['payment', 'options', 'methods'],
        feature: 'CARDS_PAYMENTS',
    },
    {
        title: 'Payouts',
        to: '/settings/payments/payouts',
        keywords: ['payouts', 'withdrawals', 'bank'],
        feature: 'CARDS_PAYMENTS',
    },
    {
        title: 'Bank Reconciliation',
        to: '/settings/payments/bank-reconciliation',
        keywords: ['bank', 'reconciliation', 'matching'],
        feature: 'BANK_RECONCILIATION',
    },
    {
        title: 'Links',
        to: '/website/links',
        keywords: ['links', 'website', 'urls'],
    },
    {
        title: 'Quote Options',
        to: '/settings/quote-options',
        keywords: ['quote', 'options', 'settings'],
    },
    {
        title: 'Website Type',
        to: '/website/type',
        keywords: ['website', 'type', 'setup'],
    },
    {
        title: 'Change Theme',
        to: '/website/theme',
        keywords: ['theme', 'website', 'design'],
    },
    {
        title: 'Update Content',
        to: '/website/content',
        keywords: ['content', 'website', 'update'],
    },
    {
        title: 'Custom Domain',
        to: '/website/custom-domain',
        keywords: ['domain', 'custom', 'website'],
        feature: 'CUSTOM_DOMAIN',
    },
    {
        title: 'Domain & Pages',
        to: '/website/third-party-settings',
        keywords: ['domain', 'pages', 'third', 'party'],
    },
    {
        title: 'Profile',
        to: '/user/profile',
        keywords: ['profile', 'account', 'user', 'name', 'email'],
    },
    {
        title: 'Security',
        to: '/user/security',
        keywords: ['security', 'password', 'change password'],
    },
    {
        title: 'App Notifications',
        to: '/user/notification-preferences',
        keywords: ['notifications', 'preferences', 'app'],
    },
    {
        title: 'Verification',
        to: '/verification',
        keywords: ['verification', 'verify', 'identity'],
    },
    {
        title: 'Close Account',
        to: '/user/close-account',
        keywords: ['close', 'account', 'delete'],
    },
    {
        title: 'Tags',
        to: '/settings/tags',
        keywords: ['tags', 'labels', 'categories'],
        feature: 'TAGS',
    },
    {
        title: 'Import',
        to: '/settings/import',
        keywords: ['import', 'data', 'upload'],
    },
    {
        title: 'Export',
        to: '/exports',
        keywords: ['export', 'download', 'data'],
    },
    {
        title: 'Reset Account',
        to: '/user/reset-account',
        keywords: ['reset', 'account', 'clear'],
    },
    {
        title: 'Additional Users',
        to: '/business/additional-users',
        keywords: ['users', 'team', 'members', 'additional'],
        feature: 'ADDITIONAL_USERS',
    },
    {
        title: 'Billing & Plan',
        to: '/billing',
        keywords: ['billing', 'plan', 'subscription', 'payment'],
    },
    {
        title: 'Help Videos',
        to: '/help/videos',
        keywords: ['help', 'videos', 'tutorials'],
    },
    {
        title: 'Getting Started Checklist',
        to: '/help/checklist',
        keywords: ['checklist', 'getting', 'started', 'guide'],
    },
    {
        title: 'Documentation',
        to: 'https://getsoapy.com/docs',
        keywords: ['documentation', 'docs', 'help'],
        external: true,
    },
    {
        title: 'What\'s New',
        to: '/whats-new',
        keywords: ['whats', 'new', 'updates', 'changelog'],
    },
    {
        title: 'Follow us on Social Media',
        to: '/get-social',
        keywords: ['social', 'media', 'follow'],
    },
    {
        title: 'Refer a Friend',
        to: '/refer-a-friend',
        keywords: ['refer', 'friend', 'referral'],
    },
    {
        title: 'Communication Preferences',
        to: '/more/communication-preferences',
        keywords: ['communication', 'preferences', 'email'],
    },
    {
        title: 'Experimental Features',
        to: '/more/experimental',
        keywords: ['experimental', 'features', 'beta'],
    },
];

export default pages;
